import { FC } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { CustomTemplatesDataTableDocument } from 'generated/resources/datatable.graphql'
import { DeleteCustomTemplateDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

export const DeleteCustomTemplateModal: FC<{ templateId: string; onClose: () => void }> = ({
  templateId,
  onClose,
}) => {
  const { company } = useCurrentSession()

  const [deleteTemplate] = useMutation(DeleteCustomTemplateDocument, {
    extractorFn: (data) => data.performanceMutations.deletePerformanceReviewProcessCustomTemplate,
    update(cache) {
      cache.evict({
        id: cache.identify({ id: templateId }),
      })
    },
  })

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const response = await deleteTemplate({
        awaitRefetchQueries: true,
        variables: {
          id: templateId,
          companyId: company.id,
        },
        refetchQueries: [
          {
            query: CustomTemplatesDataTableDocument,
            variables: {
              ids: [],
            },
          },
        ],
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) closePortal()

      return handled
    },
    {
      successMessage: i18n.t('common.removed_successfully'),
      successFlash: true,
      errorFlash: true,
    }
  )

  return (
    <Modal
      title={i18n.t('performance_process.reviews.templates.delete_modal.title')}
      description={i18n.t('performance_process.reviews.templates.delete_modal.description')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
      illustration="alert"
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}
